/**
 * The address to open for a published app, or null when it must not be
 * opened: an absolute http(s) URL, or a path that stays on `origin`. The
 * address comes from our own server, but `window.open` with a `javascript:`
 * or `data:` URL runs script, so the button checks it itself.
 *
 * Judged by what the browser's URL parser makes of it, never by its spelling:
 * the parser reads `\` as `/` and drops tabs and newlines, so `/\evil.com/`
 * and `/<tab>/evil.com/` are protocol-relative addresses that leave the
 * origin even though neither starts with `//`. The returned string is the
 * parsed one, so what opens is exactly what was checked.
 */
export function appUrlToOpen(url: string, origin: string): string | null {
  const value = url.trim();
  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
    } catch {
      return null;
    }
  }
  // Anything not rooted at `/` carries a scheme we do not open (javascript:, data:, mailto:) or is a bare relative string.
  if (!value.startsWith('/')) return null;
  try {
    const parsed = new URL(value, origin);
    return parsed.origin === new URL(origin).origin ? parsed.href : null;
  } catch {
    return null;
  }
}

/** Opens a published app in a new tab ("Open app" on the save toasts); does nothing for an address `appUrlToOpen` refuses. */
export function openAppUrl(
  url: string,
  open: (url: string, target: string, features: string) => unknown = (u, t, f) => window.open(u, t, f),
  origin: string = window.location.origin,
): boolean {
  const target = appUrlToOpen(url, origin);
  if (target === null) return false;
  open(target, '_blank', 'noopener');
  return true;
}
